import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Form,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
} from 'reactstrap';
import { useFormik } from 'formik';
import Wizard from '../../Components/Common/Wizard';
import CsvImporter from '../../Components/Common/CsvImporter';
import { useDispatch } from 'react-redux';
import { batchDeactivateUsers } from '../../slices/thunks';
import { removeBomFromBinaryFile } from '../../helpers/encode_file';

const initialValues = {
  fields: undefined,
  rows: 0,
  file: null,
};

const UserImporter = ({ isOpen, toggle, onSuccess, t, tReady }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const dispatch = useDispatch();

  const handleComplete = async (data) => {
    formik.setFieldValue('fields', data.columnFields);
    formik.setFieldValue('rows', data.rowCount);
    formik.setFieldValue('file', data.file);
    setActiveTab(2);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async () => {
      setIsLoading(true);

      try {
        const cleanFile = await removeBomFromBinaryFile(formik.values.file);

        const data = {
          file: cleanFile,
          configuration_parse: JSON.stringify({}),
        };

        const action = await dispatch(
          batchDeactivateUsers({
            ...data,
            successMessage: t('import-success'),
            errorMessage: t('import-error'),
          })
        );

        if (!action.payload.error) {
          resetState();
          onSuccess();
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  const resetState = () => {
    setTimeout(() => {
      setActiveTab(1);
      formik.resetForm();
    }, 1000);
    toggle();
  };

  return (
    <Modal
      centered
      isOpen={isOpen}
      size={activeTab >= 2 ? 'lg' : 'xl'}
      style={{ transition: 'all 0.2s ease' }}
      toggle={resetState}
      id="showModal"
    >
      <ModalHeader className="bg-info-subtle p-3" toggle={toggle}>
        {tReady && t('user-importer-title')}
      </ModalHeader>

      <Form
        className="tablelist-form"
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
          return false;
        }}
      >
        <ModalBody>
          <Wizard
            showSteps
            activeTab={activeTab}
            tabs={[
              {
                label: tReady && t('incentive-import-modal-step1-heading'),
                component: (
                  <>
                    <div className="row">
                      <div className="col-md-12">
                        <h6>
                          {tReady && t('user-importer-title')}
                        </h6>
                        <p className="d-w-100 mb-3">
                          {tReady && t('import-incentive-modal-step1-description')}
                        </p>

                        <CsvImporter
                          onClose={() => setActiveTab(2)}
                          onComplete={handleComplete}
                          fields={[
                            {
                              name: 'email',
                              label: t('email'),
                              optional: false,
                            },
                          ]}
                        />
                      </div>
                    </div>
                  </>
                ),
              },

              {
                label: tReady && t('incentive-import-modal-step3-heading'),
                component: (
                  <>
                    <h6>
                      {tReady && t('incentive-import-modal-step3-heading')}
                    </h6>
                    <p className="d-w-100">
                      {tReady && t('incentive-import-modal-step3-description')}
                    </p>

                    <Table bordered striped>
                      <tbody>
                        <tr>
                          <th style={{ width: '30%' }} scope="row">
                            {t('incentive-import-review-amount')}
                          </th>
                          <td>
                            {formik.values.rows} {t('items')}
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    <div className="alert alert-warning">
                      <strong>Atenção:</strong> {t('deactivate-users-warning')}
                    </div>

                    <div className="hstack gap-2 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={resetState}
                        disabled={isLoading}
                      >
                        {t('close')}
                      </button>

                      <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isLoading}
                      >
                        {t('confirm')}
                      </button>
                    </div>
                  </>
                ),
              },
            ]}
          />
        </ModalBody>
      </Form>
    </Modal>
  );
};

export default withTranslation()(UserImporter);